"use client"

import dynamic from "next/dynamic"
import React, { Component, useMemo } from "react"
import { HttpTypes } from "@medusajs/types"
import { SellerProps } from "@/types/seller"
import { useChat } from "@/context/ChatContext"
import { getChatAppKeys } from "@/lib/data/config"
import { useExclusiveChatSession } from "@/lib/hooks/useExclusiveChatSession"

const ConnectyCubeChatWidget = dynamic(
  () => import("@connectycube/chat-widget/react19"),
  { ssr: false }
)

class ChatWidgetErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full min-h-[200px] flex items-center justify-center text-sm text-gray-500">
          Chat is temporarily unavailable.
        </div>
      )
    }
    return this.props.children
  }
}

export interface ChatWidgetProps {
  customer: HttpTypes.StoreCustomer | null
  product: HttpTypes.StoreProduct | undefined
  chatPerProduct?: boolean
  order_id?: string
  seller: SellerProps
}

type DefaultChatTypes = "1on1" | "group"

export default function ChatWidget({
  order_id,
  customer,
  product,
  seller,
  chatPerProduct,
}: ChatWidgetProps) {
  const { setUnreadMessageCount } = useChat()
  const chatAppKeys = useMemo(() => getChatAppKeys(), [])
  const sessionReady = useExclusiveChatSession("chat-widget", 700)

  const chatPerOrder = !!order_id
  const storeId = seller?.id
  const storeName = seller?.name || "Store"

  const quickActions = useMemo(
    () => ({
      title: "Quick Actions",
      description:
        "Select an action from the options below or type a first message to start a conversation.",
      actions: chatPerProduct
        ? [
            "Hi, I'm interested in this product. ",
            "Can you tell me more about the price and payment options?",
            "Is the product still available? ",
            "Can I schedule a viewing? ",
          ]
        : chatPerOrder
          ? [
              "I have a question about my order.",
              "Can you help me track my order?",
              "Is there an issue with my order?",
              "Can I cancel or modify my order?",
            ]
          : [
              "What are your best-selling products?",
              "What payment methods do you accept?",
              "Do you offer any discounts or promotions?",
            ],
    }),
    [chatPerProduct, chatPerOrder]
  )

  const defaultChat = useMemo(() => {
    if (!storeId) return undefined
    const defaultChatKey = chatPerOrder
      ? order_id
      : chatPerProduct
        ? product?.id
        : storeId
    if (!defaultChatKey) return undefined

    const defaultChatName = chatPerOrder
      ? `Order: ${order_id} / Store: ${storeName}`
      : chatPerProduct
        ? `${product?.title || "Product"} / Store: ${storeName}`
        : storeName

    return {
      id: "" + defaultChatKey,
      opponentUserId: storeId,
      type: "1on1" as DefaultChatTypes,
      name: defaultChatName,
    }
  }, [
    storeId,
    storeName,
    chatPerOrder,
    chatPerProduct,
    order_id,
    product?.id,
    product?.title,
  ])

  const portalStyles: React.CSSProperties = {
    height: "500px",
  }

  if (!customer || !seller || !storeId) {
    return null
  }

  if (
    !sessionReady ||
    !chatAppKeys.CHAT_APP_ID ||
    !chatAppKeys.CHAT_AUTH_KEY
  ) {
    return (
      <div className="h-full min-h-[400px] flex items-center justify-center text-sm text-gray-500">
        {!chatAppKeys.CHAT_APP_ID || !chatAppKeys.CHAT_AUTH_KEY
          ? "Chat is not configured."
          : "Loading chat…"}
      </div>
    )
  }

  return (
    <div className="h-full">
      <ChatWidgetErrorBoundary>
        <ConnectyCubeChatWidget
          appId={chatAppKeys.CHAT_APP_ID + ""}
          authKey={chatAppKeys.CHAT_AUTH_KEY}
          config={{ debug: { mode: 0 } }}
          userId={customer.id}
          userName={`${customer.first_name || "Customer"} ${customer.last_name || ""}`.trim()}
          showOnlineUsersTab={false}
          splitView={false}
          portalStyle={portalStyles}
          quickActions={quickActions}
          showNotifications={true}
          playSound={true}
          enableContentReporting={true}
          enableLastSeen={true}
          enableUrlPreview={true}
          limitUrlsPreviews={1}
          attachmentsAccept={"image/*,video/*,.pdf,audio/*"}
          onUnreadCountChange={(count: number) => setUnreadMessageCount(count)}
          embedView={true}
          {...(defaultChat ? { defaultChat } : {})}
          webPushNotifications={false}
          hideNewChatButton
        />
      </ChatWidgetErrorBoundary>
    </div>
  )
}
